import { useEffect, useSyncExternalStore } from 'react';
import { isRouteErrorResponse, useRouteError } from 'react-router';
import * as Sentry from '@sentry/react';

function subscribeToConnection(onChange: () => void) {
  window.addEventListener('online', onChange);
  window.addEventListener('offline', onChange);
  return () => {
    window.removeEventListener('online', onChange);
    window.removeEventListener('offline', onChange);
  };
}

function isAssetLoadError(error: unknown) {
  return (
    error instanceof Error &&
    /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|Unable to preload CSS|Loading (?:CSS )?chunk [\w-]+ failed/i.test(
      error.message,
    )
  );
}

// Eagerly bundled: a boundary in the missing lazy chunk cannot recover that chunk.
// Reload is deliberately explicit, preserving the URL without silently discarding
// edits or starting an offline / deployment reload loop.
export function RouteErrorBoundary({
  reload = () => window.location.reload(),
}: {
  reload?: () => void;
}) {
  const error = useRouteError();
  const online = useSyncExternalStore(
    subscribeToConnection,
    () => navigator.onLine,
    () => true,
  );
  const assetFailure = isAssetLoadError(error);
  const notFound = isRouteErrorResponse(error) && error.status === 404;

  useEffect(() => {
    if (!notFound) {
      // Router catches these errors before the global handler. Report the class
      // without route data, raw exception text, stack traces or private URLs.
      Sentry.captureException(
        new Error(assetFailure ? 'Page asset failed to load' : 'Page rendering failed'),
        {
          tags: { boundary: 'route', kind: assetFailure ? 'asset' : 'render' },
        },
      );
    }
  }, [error, assetFailure, notFound]);

  return (
    <main className="min-h-screen bg-neutral-100 flex items-center justify-center p-6">
      <section
        className="w-full max-w-lg rounded-lg bg-white p-8 shadow-sm"
        aria-labelledby="recovery-heading"
      >
        <p className="text-sm font-semibold text-neutral-600 mb-3">Work@reprotec</p>
        <h1 id="recovery-heading" className="text-2xl font-semibold text-neutral-900 mb-4">
          {notFound
            ? 'Page not found'
            : assetFailure
              ? 'This page couldn’t load'
              : 'Something went wrong'}
        </h1>
        <p className="text-neutral-700 mb-4" role="status">
          {!online
            ? 'You’re offline. Reconnect to the internet, then reload this page.'
            : notFound
              ? 'Check the address and try again.'
              : assetFailure
                ? 'Your connection may have been interrupted, or Work may have been updated. Reload this page to try again.'
                : 'Reload this page to try again. If the problem continues, contact your administrator.'}
        </p>
        <p className="text-sm text-neutral-600 mb-6">
          Unsaved changes may be lost when you reload.
        </p>
        <button
          type="button"
          onClick={reload}
          disabled={!online}
          className="rounded-md bg-neutral-900 px-4 py-2.5 text-white font-medium hover:bg-neutral-700 disabled:opacity-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-neutral-900"
        >
          Reload page
        </button>
      </section>
    </main>
  );
}
